import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { EnvelopeSimple, Lock, User, ArrowRight, Warning } from "@phosphor-icons/react";
import { useAuth, formatAuthError } from "../lib/AuthContext";
import { AuthShell, Field, GoogleSignInButton } from "./LoginPage";

export default function SignupPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const { signup, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const next = new URLSearchParams(location.search).get("next") || "/";

  if (user && user !== false) {
    navigate(next, { replace: true });
    return null;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await signup(email.trim(), password, displayName.trim());
      navigate(next, { replace: true });
    } catch (err) {
      setError(formatAuthError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell title="Join Firewatch" subtitle="Get push alerts the moment a fire enters your radius.">
      <form onSubmit={onSubmit} data-testid="signup-form" className="space-y-4">
        <Field
          icon={<User size={14} weight="bold" />}
          label="Name"
          type="text"
          value={displayName}
          onChange={setDisplayName}
          autoComplete="name"
          testId="signup-name"
          required
        />
        <Field
          icon={<EnvelopeSimple size={14} weight="bold" />}
          label="Email"
          type="email"
          value={email}
          onChange={setEmail}
          autoComplete="email"
          testId="signup-email"
          required
        />
        <Field
          icon={<Lock size={14} weight="bold" />}
          label="Password"
          type="password"
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          testId="signup-password"
          required
          minLength={8}
          helper="Minimum 8 characters."
        />
        {error && (
          <div data-testid="signup-error" className="flex items-start gap-2 text-xs text-tac-danger border border-tac-danger/40 bg-tac-danger/10 rounded-sm px-3 py-2">
            <Warning size={14} weight="fill" className="shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}
        <button
          type="submit"
          disabled={submitting}
          data-testid="signup-submit"
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-tac-primary hover:bg-tac-primary/90 disabled:opacity-50 text-white font-bold uppercase tracking-wider text-xs rounded-sm transition-colors active:scale-[0.99]"
        >
          {submitting ? "Creating account…" : "Create account"}
          {!submitting && <ArrowRight size={14} weight="bold" />}
        </button>
        <p className="text-[15px] text-tac-faint text-center leading-relaxed mt-2">
          By creating an account you agree that Firewatch alerts are best-effort and
          should never replace official Sheriff / Emergency Management notifications.
        </p>
      </form>
      <GoogleSignInButton />
      <div className="text-[15px] text-tac-dim text-center mt-6">
        Already have an account?{" "}
        <Link
          to={`/login${next !== "/" ? `?next=${encodeURIComponent(next)}` : ""}`}
          className="text-tac-primary hover:underline font-bold"
          data-testid="signup-to-login-link"
        >
          Log in
        </Link>
      </div>
    </AuthShell>
  );
}
